import React, { useMemo, useState, useEffect } from "react";
import { View, Text, Card, Badge, Button, Dialog, DropdownField } from "./base";
import { Icon } from "./base/Icon";
import { TestAnalyticsDashboard } from "./charts/TestAnalyticsDashboard";
import { agentFunctionActions, AgentFunction } from "../redux/reducers/agentFunctionSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

interface TestData {
  id: number;
  publicId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  agentId: string;
  agentName: string;
  testName: string;
  functionId?: string | null;
  optionCounts: Record<
    string,
    {
      count: number;
      outcomes: Record<
        string,
        {
          type: "boolean" | "integer" | "float" | "string";
          counts: Record<string, number>;
        }
      >;
    }
  >;
  outcomes: Record<
    string,
    {
      type: "boolean" | "integer" | "float" | "string";
      counts: Record<string, number>;
    }
  >;
  sessionCount: number;
  processingStatus: "pending" | "processing" | "completed" | "failed";
  lastUpdated: string;
  errorMessage: string | null;
  stopConditions?: {
    current_confidence?: number | null;
    current_iterations?: number | null;
    max_confidence?: number | null;
    max_iterations?: number | null;
    target_outcome?: string | null;
  };
  completed?: boolean;
}

interface ProcessedOutcome {
  outcome: string;
  type: string;
  totalOutcomes: number;
  conversionRate: string;
  values: Array<{
    value: string;
    count: number;
    percentage: string;
  }>;
  successRate?: string;
  statistics?: {
    average: string;
    min: number;
    max: number;
  };
  topValue?: {
    value: string;
    count: number;
    percentage: string;
  };
  uniqueValues?: number;
}

interface ProcessedData {
  variantData: Array<{
    variant: string;
    count: number;
    percentage: string;
  }>;
  outcomeData: ProcessedOutcome[];
  outcomesByType: {
    boolean: ProcessedOutcome[];
    integer: ProcessedOutcome[];
    float: ProcessedOutcome[];
    string: ProcessedOutcome[];
  };
  totalVariants: number;
  totalOutcomes: number;
  avgOutcomesPerSession: string;
}

interface AgentTestAnalyticsProps {
  testData: TestData;
}

const formatDate = (date: string | Date) => {
  const d = new Date(date);
  return d.toLocaleString();
};

const downloadTestDataCsv = (testData: TestData) => {
  // Create CSV content
  let csvContent = "data:text/csv;charset=utf-8,";

  // Add header
  csvContent += "Test Name,Agent Name,Session Count,Processing Status,Last Updated\n";

  // Add test summary data
  csvContent += `"${testData.testName}","${testData.agentName}",${testData.sessionCount},"${testData.processingStatus}","${formatDate(testData.lastUpdated)}"\n\n`;

  // Add stop conditions if present
  if (testData.stopConditions) {
    csvContent += "Stop Conditions\n";
    csvContent += "Condition,Value\n";
    if (testData.stopConditions.max_confidence) {
      csvContent += `"Max Confidence","${testData.stopConditions.max_confidence}"\n`;
    }
    if (testData.stopConditions.max_iterations) {
      csvContent += `"Max Iterations","${testData.stopConditions.max_iterations}"\n`;
    }
    if (testData.stopConditions.target_outcome) {
      csvContent += `"Target Outcome","${testData.stopConditions.target_outcome}"\n`;
    }
    if (testData.stopConditions.current_confidence) {
      csvContent += `"Current Confidence","${testData.stopConditions.current_confidence}"\n`;
    }
    if (testData.stopConditions.current_iterations) {
      csvContent += `"Current Iterations","${testData.stopConditions.current_iterations}"\n`;
    }
    csvContent += "\n";
  }

  // Add variant distribution
  csvContent += "Variant Distribution\n";
  csvContent += "Variant,Session Count,Percentage\n";
  Object.entries(testData.optionCounts).forEach(([variant, option]) => {
    const percentage = testData.sessionCount > 0 ? ((option.count / testData.sessionCount) * 100).toFixed(1) : "0.0";
    csvContent += `"${variant}",${option.count},${percentage}%\n`;
  });

  csvContent += "\n";

  // Add outcomes data
  csvContent += "Outcomes Data\n";
  csvContent += "Outcome Name,Type,Total Count\n";
  Object.entries(testData.outcomes).forEach(([outcomeName, outcomeData]) => {
    const totalCount = Object.values(outcomeData.counts).reduce((sum, count) => sum + count, 0);
    csvContent += `"${outcomeName}","${outcomeData.type}",${totalCount}\n`;
  });

  csvContent += "\n";

  // Add detailed outcome values
  csvContent += "Detailed Outcome Values\n";
  csvContent += "Outcome Name,Value,Count\n";
  Object.entries(testData.outcomes).forEach(([outcomeName, outcomeData]) => {
    Object.entries(outcomeData.counts).forEach(([value, count]) => {
      csvContent += `"${outcomeName}","${value}",${count}\n`;
    });
  });

  // Create and trigger download
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", `test_analytics_${testData.testName.replace(/[^a-zA-Z0-9]/g, "_")}_${new Date().toISOString().split("T")[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const AgentTestAnalytics: React.FC<AgentTestAnalyticsProps> = ({ testData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const agentFunctions: AgentFunction[] = useSelector((state: any) => Object.values(state.agentFunction?.entities || {}));
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFunctionId, setSelectedFunctionId] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);

  // Load functions for this agent when component mounts
  useEffect(() => {
    if (testData.agentId) {
      dispatch(agentFunctionActions.get({ agentId: testData.agentId } as any) as any);
    }
  }, [dispatch, testData.agentId]);

  const functionOptions = useMemo(() => {
    const sorted = [...(agentFunctions || [])].sort((a, b) => {
      const aTime = new Date(a.updatedAt as any).getTime();
      const bTime = new Date(b.updatedAt as any).getTime();
      return bTime - aTime;
    });
    return sorted.map((f) => ({ label: f.name, value: f.id }));
  }, [agentFunctions]);

  const handleOpenDialog = () => {
    setSelectedFunctionId(testData.functionId ?? "");
    setIsDialogOpen(true);
  };

  const handleGenerate = async () => {
    if (!selectedFunctionId) return;
    setIsGenerating(true);
    try {
      // Navigate to the function page and signal auto-improve-on-load with this test
      const search = new URLSearchParams({ f: selectedFunctionId, improveFromTest: testData.testName }).toString();
      navigate(`/agents/${testData.agentId}/function?${search}`);
      setIsDialogOpen(false);
    } finally {
      setIsGenerating(false);
    }
  };
  // Process data for visualizations
  const processedData: ProcessedData = useMemo(() => {
    // Ensure optionCounts and outcomes are not null/undefined
    const optionCounts = testData.optionCounts || {};
    const outcomes = testData.outcomes || {};
    const sessionCount = testData.sessionCount || 0;

    // Variant distribution data
    const variantData = Object.entries(optionCounts).map(([variant, option]) => ({
      variant,
      count: option.count,
      percentage: sessionCount > 0 ? ((option.count / sessionCount) * 100).toFixed(1) : "0.0",
    }));

    // Process outcomes by type
    const outcomesByType = {
      boolean: [] as ProcessedOutcome[],
      integer: [] as ProcessedOutcome[],
      float: [] as ProcessedOutcome[],
      string: [] as ProcessedOutcome[],
    };

    const processedOutcomes = Object.entries(outcomes).map(([outcome, data]) => {
      const { type, counts } = data;
      const countsObj = counts || {};
      const totalOutcomes = Object.values(countsObj).reduce((sum: number, count: number) => sum + count, 0);
      const conversionRate = sessionCount > 0 ? ((totalOutcomes / sessionCount) * 100).toFixed(1) : "0.0";

      let processedOutcome: ProcessedOutcome = {
        outcome,
        type,
        totalOutcomes,
        conversionRate,
        values: Object.entries(countsObj).map(([value, count]) => ({
          value: value || "empty",
          count,
          percentage: totalOutcomes > 0 ? ((count / totalOutcomes) * 100).toFixed(1) : "0.0",
        })),
      };

      // Type-specific processing
      if (type === "boolean") {
        const trueCount = (countsObj as Record<string, number>)["true"] || 0;
        const falseCount = (countsObj as Record<string, number>)["false"] || 0;
        processedOutcome.successRate = trueCount + falseCount > 0 ? ((trueCount / (trueCount + falseCount)) * 100).toFixed(1) : "0.0";
        outcomesByType.boolean.push(processedOutcome);
      } else if (type === "integer") {
        const numericValues = Object.entries(countsObj).map(([val, count]) => ({
          value: parseFloat(val) || 0,
          count,
          weightedValue: (parseFloat(val) || 0) * count,
        }));

        const totalWeightedSum = numericValues.reduce((sum, item) => sum + item.weightedValue, 0);
        const average = totalOutcomes > 0 ? (totalWeightedSum / totalOutcomes).toFixed(2) : "0.00";

        const sortedValues = numericValues.sort((a, b) => a.value - b.value);
        const min = sortedValues[0]?.value || 0;
        const max = sortedValues[sortedValues.length - 1]?.value || 0;

        processedOutcome.statistics = { average, min, max };
        outcomesByType.integer.push(processedOutcome);
      } else if (type === "float") {
        const numericValues = Object.entries(countsObj).map(([val, count]) => ({
          value: parseFloat(val) || 0,
          count,
          weightedValue: (parseFloat(val) || 0) * count,
        }));

        const totalWeightedSum = numericValues.reduce((sum, item) => sum + item.weightedValue, 0);
        const average = totalOutcomes > 0 ? (totalWeightedSum / totalOutcomes).toFixed(2) : "0.00";

        const sortedValues = numericValues.sort((a, b) => a.value - b.value);
        const min = sortedValues[0]?.value || 0;
        const max = sortedValues[sortedValues.length - 1]?.value || 0;

        processedOutcome.statistics = { average, min, max };
        outcomesByType.float.push(processedOutcome);
      } else if (type === "string") {
        const sortedValues = processedOutcome.values.sort((a, b) => b.count - a.count);
        processedOutcome.topValue = sortedValues[0];
        processedOutcome.uniqueValues = sortedValues.length;
        outcomesByType.string.push(processedOutcome);
      }

      return processedOutcome;
    });

    // Performance summary
    const totalVariants = Object.keys(optionCounts).length;
    const totalOutcomes = Object.keys(outcomes).length;
    const avgOutcomesPerSession =
      sessionCount > 0
        ? (
            Object.values(outcomes).reduce((sum, data) => {
              const counts = data.counts || {};
              return sum + Object.values(counts).reduce((s: number, c: number) => s + c, 0);
            }, 0) / sessionCount
          ).toFixed(2)
        : "0.00";

    return {
      variantData,
      outcomeData: processedOutcomes,
      outcomesByType,
      totalVariants,
      totalOutcomes,
      avgOutcomesPerSession,
    };
  }, [testData]);

  const TypeIcon: React.FC<{ type: string }> = ({ type }) => {
    switch (type) {
      case "bool":
        return (
          <Icon
            name="indeterminate"
            size={16}
          />
        );
      case "integer":
        return (
          <Icon
            name="number"
            size={16}
          />
        );
      case "float":
        return (
          <Icon
            name="decimal"
            size={16}
          />
        );
      case "string":
        return (
          <Icon
            name="text"
            size={16}
          />
        );
      default:
        return (
          <Icon
            name="activity"
            size={16}
          />
        );
    }
  };

  const BooleanOutcomeCard: React.FC<{ outcome: ProcessedOutcome }> = ({ outcome }) => (
    <Card className="bg-info/10 dark:bg-info/20 border-info/20 dark:border-info/30">
      <View className="p-4">
        <View className="flex items-center justify-between mb-3">
          <View className="flex items-center space-x-2">
            <TypeIcon type="bool" />
            <Text className="font-medium">{outcome.outcome}</Text>
          </View>
          <Text className="text-sm text-info-dark dark:text-info-light font-medium">{outcome.successRate}% success</Text>
        </View>

        <View className="space-y-2">
          {outcome.values.map((item, idx) => (
            <View
              key={`boolean-${outcome.outcome}-${item.value}-${idx}`}
              className="flex justify-between items-center text-sm"
            >
              <View
                className={`px-2 py-1 rounded text-xs font-medium ${
                  item.value === "true"
                    ? "bg-success/10 text-success-dark dark:bg-success/20 dark:text-success-light"
                    : "bg-error/10 text-error-dark dark:bg-error/20 dark:text-error-light"
                }`}
              >
                <Text>{item.value}</Text>
              </View>
              <View className="flex items-center space-x-2">
                <Text className="font-medium">{item.count}</Text>
                <Text className="text-gray-400">({item.percentage}%)</Text>
              </View>
            </View>
          ))}
        </View>

        <View className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
          <View className="flex justify-between text-sm">
            <Text>Conversion Rate:</Text>
            <Text className="font-medium">{outcome.conversionRate}%</Text>
          </View>
        </View>
      </View>
    </Card>
  );

  const IntegerOutcomeCard: React.FC<{ outcome: ProcessedOutcome }> = ({ outcome }) => (
    <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
      <View className="p-4">
        <View className="flex items-center justify-between mb-3">
          <View className="flex items-center space-x-2">
            <TypeIcon type="integer" />
            <Text className="font-medium">{outcome.outcome}</Text>
          </View>
          <Text className="text-sm text-purple-600 dark:text-purple-400 font-medium">Avg: {outcome.statistics?.average}</Text>
        </View>

        <View className="grid grid-cols-3 gap-2 mb-3">
          <View className="text-center p-2 bg-white dark:bg-gray-800 rounded">
            <Text className="text-xs text-gray-500 dark:text-gray-400">Min</Text>
            <Text className="font-medium">{outcome.statistics?.min}</Text>
          </View>
          <View className="text-center p-2 bg-white dark:bg-gray-800 rounded">
            <Text className="text-xs text-gray-500 dark:text-gray-400">Avg</Text>
            <Text className="font-medium">{outcome.statistics?.average}</Text>
          </View>
          <View className="text-center p-2 bg-white dark:bg-gray-800 rounded">
            <Text className="text-xs text-gray-500 dark:text-gray-400">Max</Text>
            <Text className="font-medium">{outcome.statistics?.max}</Text>
          </View>
        </View>

        <View className="space-y-2">
          {outcome.values.map((item, idx) => (
            <View
              key={`integer-${outcome.outcome}-${item.value}-${idx}`}
              className="flex justify-between items-center text-sm"
            >
              <Text className="text-gray-600 dark:text-gray-400">{item.value}:</Text>
              <View className="flex items-center space-x-2">
                <Text className="font-medium">{item.count}</Text>
                <Text className="text-gray-400">({item.percentage}%)</Text>
              </View>
            </View>
          ))}
        </View>

        <View className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
          <View className="flex justify-between text-sm">
            <Text>Conversion Rate:</Text>
            <Text className="font-medium">{outcome.conversionRate}%</Text>
          </View>
        </View>
      </View>
    </Card>
  );

  const FloatOutcomeCard: React.FC<{ outcome: ProcessedOutcome }> = ({ outcome }) => (
    <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
      <View className="p-4">
        <View className="flex items-center justify-between mb-3">
          <View className="flex items-center space-x-2">
            <TypeIcon type="float" />
            <Text className="font-medium">{outcome.outcome}</Text>
          </View>
          <Text className="text-sm text-purple-600 dark:text-purple-400 font-medium">Avg: {outcome.statistics?.average}</Text>
        </View>

        <View className="grid grid-cols-3 gap-2 mb-3">
          <View className="text-center p-2 bg-white dark:bg-gray-800 rounded">
            <Text className="text-xs text-gray-500 dark:text-gray-400">Min</Text>
            <Text className="font-medium">{outcome.statistics?.min}</Text>
          </View>
          <View className="text-center p-2 bg-white dark:bg-gray-800 rounded">
            <Text className="text-xs text-gray-500 dark:text-gray-400">Avg</Text>
            <Text className="font-medium">{outcome.statistics?.average}</Text>
          </View>
          <View className="text-center p-2 bg-white dark:bg-gray-800 rounded">
            <Text className="text-xs text-gray-500 dark:text-gray-400">Max</Text>
            <Text className="font-medium">{outcome.statistics?.max}</Text>
          </View>
        </View>

        <View className="space-y-2">
          {outcome.values.map((item, idx) => (
            <View
              key={`float-${outcome.outcome}-${item.value}-${idx}`}
              className="flex justify-between items-center text-sm"
            >
              <Text className="text-gray-600 dark:text-gray-400">{item.value}:</Text>
              <View className="flex items-center space-x-2">
                <Text className="font-medium">{item.count}</Text>
                <Text className="text-gray-400">({item.percentage}%)</Text>
              </View>
            </View>
          ))}
        </View>

        <View className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
          <View className="flex justify-between text-sm">
            <Text>Conversion Rate:</Text>
            <Text className="font-medium">{outcome.conversionRate}%</Text>
          </View>
        </View>
      </View>
    </Card>
  );

  const StringOutcomeCard: React.FC<{ outcome: ProcessedOutcome }> = ({ outcome }) => (
    <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
      <View className="p-4">
        <View className="flex items-center justify-between mb-3">
          <View className="flex items-center space-x-2">
            <TypeIcon type="string" />
            <Text className="font-medium">{outcome.outcome}</Text>
          </View>
          <Text className="text-sm text-green-600 dark:text-green-400 font-medium">{outcome.uniqueValues} unique</Text>
        </View>

        <View className="mb-3 p-2 bg-white dark:bg-gray-800 rounded">
          <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">Most Common</Text>
          <View className="flex justify-between items-center">
            <Text className="font-medium text-green-700 dark:text-green-300">{outcome.topValue?.value}</Text>
            <Text className="text-sm">({outcome.topValue?.percentage}%)</Text>
          </View>
        </View>

        <View className="space-y-2">
          {outcome.values.map((item, idx) => (
            <View
              key={`string-${outcome.outcome}-${item.value}-${idx}`}
              className="flex justify-between items-center text-sm"
            >
              <div
                className="text-gray-600 dark:text-gray-400 truncate max-w-24"
                title={item.value}
              >
                <Text>{item.value}:</Text>
              </div>
              <View className="flex items-center space-x-2">
                <Text className="font-medium">{item.count}</Text>
                <Text className="text-gray-400">({item.percentage}%)</Text>
              </View>
            </View>
          ))}
        </View>

        <View className="mt-3 pt-3 border-t border-green-200 dark:border-green-700">
          <View className="flex justify-between text-sm">
            <Text>Conversion Rate:</Text>
            <Text className="font-medium">{outcome.conversionRate}%</Text>
          </View>
        </View>
      </View>
    </Card>
  );

  const StatusBadge: React.FC<{ completed: boolean }> = ({ completed }) => {
    return (
      <View
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          completed
            ? "bg-success/10 text-success-dark dark:bg-success/20 dark:text-success-light"
            : "bg-warning/10 text-warning-dark dark:bg-warning/20 dark:text-warning-light"
        }`}
      >
        <Icon
          name={completed ? "check-circle" : "run"}
          size={12}
          className="mr-1"
        />
        <Badge variant={completed ? "success" : "warning"}>{completed ? "Completed" : "Running"}</Badge>
      </View>
    );
  };

  const StopConditionsPanel: React.FC<{ stopConditions: TestData["stopConditions"] }> = ({ stopConditions }) => {
    if (!stopConditions) return null;

    // Check if any stop conditions are set
    const hasMaxConfidence = stopConditions.max_confidence && stopConditions.max_confidence > 0;
    const hasMaxIterations = stopConditions.max_iterations && stopConditions.max_iterations > 0;

    if (!hasMaxConfidence && !hasMaxIterations) return null;

    return (
      <View className="mb-8">
        <View className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {hasMaxIterations && (
            <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
              <View className="p-4">
                <View className="flex items-center justify-between mb-3">
                  <View className="flex items-center space-x-2">
                    <Icon
                      name="refresh"
                      size={16}
                      className="text-purple-600 dark:text-purple-400"
                    />
                    <Text className="font-medium">Iteration Limit</Text>
                  </View>
                  <Text className="text-sm text-purple-600 dark:text-purple-400 font-medium">Max: {stopConditions.max_iterations}</Text>
                </View>

                {stopConditions.current_iterations && (
                  <View className="mb-3 p-2 bg-white dark:bg-gray-800 rounded">
                    <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current Progress</Text>
                    <View className="flex justify-between items-center">
                      <Text className="font-medium text-purple-700 dark:text-purple-300">{stopConditions.current_iterations}</Text>
                      <Text className="text-sm text-purple-600 dark:text-purple-400">of {stopConditions.max_iterations || 0}</Text>
                    </View>
                  </View>
                )}

                {stopConditions.target_outcome && <div style={{ height: "35px" }} />}

                <View className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                  <View className="flex justify-between text-sm">
                    <Text>Status:</Text>
                    <Text className="font-medium">
                      {stopConditions.current_iterations && stopConditions.max_iterations && stopConditions.current_iterations >= stopConditions.max_iterations
                        ? "Limit Reached"
                        : "Active"}
                    </Text>
                  </View>
                </View>
              </View>
            </Card>
          )}

          {hasMaxConfidence && (
            <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <View className="p-4">
                <View className="flex items-center justify-between mb-3">
                  <View className="flex items-center space-x-2">
                    <Icon
                      name="target"
                      size={16}
                      className="text-info-dark dark:text-info-light"
                    />
                    <Text className="font-medium">Confidence Target</Text>
                  </View>
                  <Text className="text-sm text-info-dark dark:text-info-light font-medium">{stopConditions.max_confidence}%</Text>
                </View>

                {stopConditions.current_confidence && (
                  <View className="mb-3 p-2 bg-white dark:bg-gray-800 rounded">
                    <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current Progress</Text>
                    <View className="flex justify-between items-center">
                      <Text className="font-medium text-blue-700 dark:text-blue-300">{stopConditions.current_confidence.toFixed(1)}%</Text>
                      <Text className="text-sm text-info-dark dark:text-info-light">of {stopConditions.max_confidence || 0}%</Text>
                    </View>
                  </View>
                )}

                {stopConditions.target_outcome && (
                  <View className="mb-3 p-2 bg-white dark:bg-gray-800 rounded">
                    <View className="flex justify-between items-center">
                      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">Target Outcome</Text>
                      <Text className="text-sm text-info-dark dark:text-info-light">{stopConditions.target_outcome}</Text>
                    </View>
                  </View>
                )}

                <View className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                  <View className="flex justify-between text-sm">
                    <Text>Status:</Text>
                    <Text className="font-medium">
                      {stopConditions.current_confidence && stopConditions.max_confidence && stopConditions.current_confidence >= stopConditions.max_confidence
                        ? "Target Reached"
                        : "Active"}
                    </Text>
                  </View>
                </View>
              </View>
            </Card>
          )}
        </View>
      </View>
    );
  };

  const OutcomeTypesSummary: React.FC = () => (
    <View className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <View className="p-4">
          <View className="flex items-center justify-between mb-2">
            <View className="flex items-center space-x-2">
              <Icon
                name="indeterminate"
                size={20}
                className="text-info-dark dark:text-info-light"
              />
              <Text className="font-medium text-blue-900 dark:text-blue-100">Boolean Outcomes</Text>
            </View>
            <Text className="text-info-dark dark:text-info-light font-bold">{processedData.outcomesByType.boolean.length}</Text>
          </View>
          <Text className="text-sm text-blue-700 dark:text-blue-300">Success/failure tracking</Text>
        </View>
      </Card>

      <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
        <View className="p-4">
          <View className="flex items-center justify-between mb-2">
            <View className="flex items-center space-x-2">
              <Icon
                name="number"
                size={20}
                className="text-purple-600 dark:text-purple-400"
              />
              <Text className="font-medium text-purple-900 dark:text-purple-100">Integer Outcomes</Text>
            </View>
            <Text className="text-purple-600 dark:text-purple-400 font-bold">{processedData.outcomesByType.integer.length}</Text>
          </View>
          <Text className="text-sm text-purple-700 dark:text-purple-300">Integer values & averages</Text>
        </View>
      </Card>

      <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
        <View className="p-4">
          <View className="flex items-center justify-between mb-2">
            <View className="flex items-center space-x-2">
              <Icon
                name="decimal"
                size={20}
                className="text-purple-600 dark:text-purple-400"
              />
              <Text className="font-medium text-purple-900 dark:text-purple-100">Float Outcomes</Text>
            </View>
            <Text className="text-purple-600 dark:text-purple-400 font-bold">{processedData.outcomesByType.float.length}</Text>
          </View>
          <Text className="text-sm text-purple-700 dark:text-purple-300">Float values & averages</Text>
        </View>
      </Card>

      <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
        <View className="p-4">
          <View className="flex items-center justify-between mb-2">
            <View className="flex items-center space-x-2">
              <Icon
                name="text"
                size={20}
                className="text-green-600 dark:text-green-400"
              />
              <Text className="font-medium text-green-900 dark:text-green-100">String Outcomes</Text>
            </View>
            <Text className="text-green-600 dark:text-green-400 font-bold">{processedData.outcomesByType.string.length}</Text>
          </View>
          <Text className="text-sm text-green-700 dark:text-green-300">Categorical responses</Text>
        </View>
      </Card>
    </View>
  );

  return (
    <View className="bg-gray-50 dark:bg-gray-900 p-6">
      <View className="max-w-7xl mx-auto">
        {/* Dialog */}
        <Dialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          title="Use Test Results"
          actions={
            <>
              <Button
                onClick={() => setIsDialogOpen(false)}
                variant="ghost"
                type="button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                isDisabled={!selectedFunctionId || isGenerating}
                isLoading={isGenerating}
              >
                Generate
              </Button>
            </>
          }
        >
          <View className="p-4 space-y-4">
            <Text className="text-sm text-gray-600 dark:text-gray-300">Select a function to improve using the results from this completed test.</Text>
            <DropdownField
              label="Function"
              value={selectedFunctionId}
              onChange={(val) => setSelectedFunctionId(val as string)}
              options={functionOptions}
              placeholder="Select function"
            />
          </View>
        </Dialog>
        {/* Header */}
        <View className="mb-8">
          <View className="flex justify-between items-start mb-4">
            <View>
              <Text className="text-3xl font-bold">{testData.testName}</Text>
            </View>
            <View className="flex flex-col items-end">
              <StatusBadge completed={testData.completed ?? false} />
            </View>
          </View>

          {/* Test Info */}
          <View className="grid grid-cols-2">
            {/* Column 1: Info */}
            <View>
              <View className="flex items-center text-gray-500 dark:text-gray-400 space-x-4">
                <Text>Test ID: {testData.publicId}</Text>
              </View>
              <View className="flex items-center text-gray-500 dark:text-gray-400 space-x-4">
                <Text>Agent: {testData.agentName}</Text>
              </View>

              <View className="flex items-center text-gray-500 dark:text-gray-400 space-x-4">
                <Text>Created: {formatDate(testData.createdAt)}</Text>
              </View>
              <View className="flex items-center text-gray-500 dark:text-gray-400 space-x-4">
                <Text>Last Updated: {formatDate(testData.lastUpdated)}</Text>
              </View>
            </View>

            {/* Column 2: Actions */}
            <View className="flex flex-col items-end">
              {/* Use Results to Improve Function */}
              {testData.completed && (
                <Button
                  onClick={handleOpenDialog}
                  variant="outline"
                  className="mt-2 text-sm mb-4"
                >
                  <Icon
                    name="zap"
                    size={16}
                    className="mr-2"
                  />
                  Use Results to Improve Function
                </Button>
              )}

              {/* Download CSV */}
              <Button
                onClick={() => downloadTestDataCsv(testData)}
                variant="ghost"
                className="flex items-center !py-1 !px-2 text-xs"
              >
                <Icon
                  name="download"
                  size={14}
                  className="text-gray-400 mt-[2px] mr-1"
                />
                <Text className="text-xs">Download CSV</Text>
              </Button>
            </View>
          </View>
        </View>

        {/* Key Metrics */}
        <View className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card>
            <View className="p-6">
              <View className="flex items-center justify-between mb-2">
                <View className="flex items-center space-x-6">
                  <Icon
                    name="users"
                    size={32}
                    className="text-info-dark dark:text-info-light"
                  />
                  <Text className="text-xl font-medium text-gray-600 dark:text-gray-400">Total Sessions</Text>
                </View>
                <Text className="text-2xl font-bold">{testData.sessionCount}</Text>
              </View>
            </View>
          </Card>

          <Card>
            <View className="p-6">
              <View className="flex items-center justify-between mb-2">
                <View className="flex items-center space-x-6">
                  <Icon
                    name="variants"
                    size={32}
                    className="text-green-600 dark:text-green-400"
                  />
                  <Text className="text-xl font-medium text-gray-600 dark:text-gray-400">Variants</Text>
                </View>
                <Text className="text-2xl font-bold">{processedData.totalVariants}</Text>
              </View>
            </View>
          </Card>

          <Card>
            <View className="p-6">
              <View className="flex items-center justify-between mb-2">
                <View className="flex items-center space-x-6">
                  <Icon
                    name="activity"
                    size={32}
                    className="text-purple-600 dark:text-purple-400"
                  />
                  <Text className="text-xl font-medium text-gray-600 dark:text-gray-400">Outcomes Tracked</Text>
                </View>
                <Text className="text-2xl font-bold">{processedData.totalOutcomes}</Text>
              </View>
            </View>
          </Card>
        </View>

        {/* Outcome Types Summary */}
        <OutcomeTypesSummary />

        {/* Stop Conditions Panel */}
        <StopConditionsPanel stopConditions={testData.stopConditions} />

        {/* Test Analytics Dashboard */}
        <TestAnalyticsDashboard
          optionCounts={testData.optionCounts}
          outcomes={testData.outcomes}
          sessionCount={testData.sessionCount}
        />

        {/* Outcome Analysis by Type */}
        {processedData.outcomesByType.boolean.length > 0 && (
          <Card className="mb-8">
            <View className="p-6">
              <View className="flex items-center mb-6">
                <Icon
                  name="indeterminate"
                  size={20}
                  className="mr-2 text-info-dark dark:text-info-light"
                />
                <Text className="text-lg font-semibold">Boolean Outcomes</Text>
              </View>
              <View className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {processedData.outcomesByType.boolean.map((outcome: ProcessedOutcome) => (
                  <BooleanOutcomeCard
                    key={outcome.outcome}
                    outcome={outcome}
                  />
                ))}
              </View>
            </View>
          </Card>
        )}

        {processedData.outcomesByType.integer.length > 0 && (
          <Card className="mb-8">
            <View className="p-6">
              <View className="flex items-center mb-6">
                <Icon
                  name="number"
                  size={20}
                  className="mr-2 text-purple-600 dark:text-purple-400"
                />
                <Text className="text-lg font-semibold">Integer Outcomes</Text>
              </View>
              <View className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {processedData.outcomesByType.integer.map((outcome: ProcessedOutcome) => (
                  <IntegerOutcomeCard
                    key={outcome.outcome}
                    outcome={outcome}
                  />
                ))}
              </View>
            </View>
          </Card>
        )}

        {processedData.outcomesByType.float.length > 0 && (
          <Card className="mb-8">
            <View className="p-6">
              <View className="flex items-center mb-6">
                <Icon
                  name="decimal"
                  size={20}
                  className="mr-2 text-purple-600 dark:text-purple-400"
                />
                <Text className="text-lg font-semibold">Float Outcomes</Text>
              </View>
              <View className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {processedData.outcomesByType.float.map((outcome: ProcessedOutcome) => (
                  <FloatOutcomeCard
                    key={outcome.outcome}
                    outcome={outcome}
                  />
                ))}
              </View>
            </View>
          </Card>
        )}

        {processedData.outcomesByType.string.length > 0 && (
          <Card className="mb-8">
            <View className="p-6">
              <View className="flex items-center mb-6">
                <Icon
                  name="text"
                  size={20}
                  className="mr-2 text-green-600 dark:text-green-400"
                />
                <Text className="text-lg font-semibold">String Outcomes</Text>
              </View>
              <View className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {processedData.outcomesByType.string.map((outcome) => (
                  <StringOutcomeCard
                    key={outcome.outcome}
                    outcome={outcome}
                  />
                ))}
              </View>
            </View>
          </Card>
        )}
      </View>
    </View>
  );
};
